import { useSyncExternalStore } from 'react'
import { API_BASE_URL } from './api'

// Online/offline state for the mail offline mode (#785). `navigator.onLine` alone
// is not enough (it stays true in a dead spot with WLAN but no route), so a fetch
// network error (TypeError) also counts as offline until a request goes through
// again or the browser fires `online`. No polling in the second range: at most one
// health probe every 30 s while in the network-error state.

export interface OnlineEnv {
  navigator?: { onLine?: boolean }
  addEventListener?: (t: string, l: () => void) => void
  removeEventListener?: (t: string, l: () => void) => void
  /** Optional reachability probe used while a network error is pending
   * (true = server reachable). Without it nothing is scheduled. */
  probe?: () => Promise<boolean>
  /** Probe interval, default 30 s. */
  probeIntervalMs?: number
}

export interface OnlineStatus {
  /** navigator.onLine !== false AND no pending network error. */
  isOnline(): boolean
  /** A fetch TypeError was seen → offline until reportSuccess / the `online` event. */
  reportNetworkError(): void
  reportSuccess(): void
  subscribe(listener: (online: boolean) => void): () => void
}

const DEFAULT_PROBE_INTERVAL_MS = 30_000

/** The single place that decides whether an error means "no connection":
 * fetch rejects with a TypeError; our `request` errors always carry a numeric
 * HTTP `status`. Anything else without a status (abort, CORS/network) counts too. */
export function isNetworkError(err: unknown): boolean {
  if (err instanceof TypeError) return true
  if (!(err instanceof Error)) return false
  return typeof (err as { status?: unknown }).status !== 'number'
}

export function createOnlineStatus(env: OnlineEnv = {}): OnlineStatus {
  const listeners = new Set<(online: boolean) => void>()
  let networkError = false
  let last = compute()
  let probeTimer: ReturnType<typeof setInterval> | null = null
  const interval = env.probeIntervalMs ?? DEFAULT_PROBE_INTERVAL_MS

  function compute(): boolean {
    return env.navigator?.onLine !== false && !networkError
  }

  function emit(): void {
    const now = compute()
    if (now === last) return
    last = now
    for (const listener of [...listeners]) {
      try {
        listener(now)
      } catch {
        // a broken listener must not stop the others
      }
    }
  }

  function stopProbe(): void {
    if (probeTimer !== null) clearInterval(probeTimer)
    probeTimer = null
  }

  function startProbe(): void {
    const probe = env.probe
    if (!probe || probeTimer !== null) return
    probeTimer = setInterval(() => {
      // Only probe when the browser itself believes it is online.
      if (!networkError || env.navigator?.onLine === false) return
      probe().then(
        (ok) => {
          if (ok) status.reportSuccess()
        },
        () => {
          // still unreachable
        },
      )
    }, interval)
  }

  const status: OnlineStatus = {
    isOnline: compute,
    reportNetworkError() {
      networkError = true
      startProbe()
      emit()
    },
    reportSuccess() {
      networkError = false
      stopProbe()
      emit()
    },
    subscribe(listener) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }

  env.addEventListener?.('online', () => {
    networkError = false
    stopProbe()
    emit()
  })
  env.addEventListener?.('offline', () => emit())

  return status
}

function browserEnv(): OnlineEnv | undefined {
  if (typeof window === 'undefined') return undefined
  return {
    navigator: window.navigator,
    addEventListener: (t, l) => window.addEventListener(t, l),
    removeEventListener: (t, l) => window.removeEventListener(t, l),
    probe: async () => {
      // Same base as every API call (dev: VITE_API_URL, prod: document base incl. /zeit/).
      const res = await fetch(`${API_BASE_URL}/api/health`, {
        cache: 'no-store',
        credentials: 'omit',
      })
      return res.ok
    },
  }
}

/** App-wide instance, bound to `window` when present. */
export const onlineStatus: OnlineStatus = createOnlineStatus(browserEnv())

function getServerSnapshot(): boolean {
  return true
}

/** React hook: true while online (re-renders on every change). */
export function useOnline(): boolean {
  return useSyncExternalStore(onlineStatus.subscribe, onlineStatus.isOnline, getServerSnapshot)
}
